import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, Phone, FileText, MessageCircle, Plus } from 'lucide-react';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../../AuthContext';
import { fetchUserNotes, createNote, updateNote, deleteNote as deleteNoteApi, fetchMailboxItems } from '../../../services/resourceService';
import { stripHtml } from '../../../utils/textUtils';
import { MiniLoader } from '../../../components/Common/LoadingScreen';
import EmptyState from '../../../components/Common/SafeApp/EmptyState';
import { MessageSquare, ClipboardList, PenTool } from 'lucide-react';

export const FacultyInfoCard = () => {
  const { facultyInfo, user } = useAuth();

  const name = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Unknown Faculty';
  const role = user?.userType || 'Faculty';
  const department = facultyInfo?.department || 'General Academics';
  const employeeId = facultyInfo?.employeeId || 'FAC-N/A';
  const email = user?.email || 'N/A';
  const phone = user?.phoneNumber || 'N/A';
  const joinedDate = facultyInfo?.joiningDate
    ? new Date(facultyInfo.joiningDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'N/A';

  return (
    <div className="profile-info-card fic-card">
      <div className="profile-info-header">
        <div className="profile-info-avatar">
          {user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'F'}
          <div className="avatar-status-dot"></div>
        </div>
        <div className="profile-info-identity">
          <div className="identity-top">
            <span className="profile-role-badge">{role}</span>
          </div>
          <h2 className="profile-name">{name}</h2>
          <p className="profile-id-tag">Emp ID: {employeeId}</p>
        </div>
      </div>

      <div className="profile-info-grid">
        <div className="info-grid-item">
          <label>Department</label>
          <span>{department}</span>
        </div>
        <div className="info-grid-item">
          <label>Joining Date</label>
          <span>{joinedDate}</span>
        </div>
        <div className="info-grid-item">
          <label>Email Address</label>
          <span title={email}>{email}</span>
        </div>
        <div className="info-grid-item">
          <label>Phone Number</label>
          <span>{phone}</span>
        </div>
      </div>
    </div>
  );
};

export const SubjectProgressCard = ({
  className = 'Class 8A',
  subject = 'Science',
  progress = 68,
  completed = 12,
  total = 18,
}) => {
  const progressPercentage = (completed / total) * 100;

  return (
    <div className="spc-card">
      <div className="spc-header">
        <h3>Subject Progress</h3>
        <p className="spc-class">{className} - {subject}</p>
      </div>
      <div className="spc-progress-circle">
        <div
          className="spc-circle-bg"
          style={{ background: `conic-gradient(#48bb78 ${progressPercentage}deg, #e2e8f0 ${progressPercentage}deg)` }}
        >
          <div className="spc-circle-inner">
            <span className="spc-percentage">{progress}%</span>
          </div>
        </div>
      </div>
      <div className="spc-legend">
        <div className="spc-legend-item spc-done">
          <span className="spc-dot spc-done"></span>
          <span>Done</span>
          <span className="spc-count">{completed}/{total}</span>
        </div>
        <div className="spc-legend-item spc-progress">
          <span className="spc-dot spc-progress"></span>
          <span>Progress</span>
          <span className="spc-count">{total - completed} left</span>
        </div>
      </div>
    </div>
  );
};

export const QuickActionsCard = () => {
  const actions = [
    { icon: Upload, label: 'Upload Resources', description: 'Upload teaching materials', priority: 'low' },
    { icon: Download, label: 'Download Syllabus', description: 'Get latest syllabus', priority: 'low' },
    { icon: Phone, label: 'Contact Admin', description: 'Reach out to administration', priority: 'medium' },
    { icon: FileText, label: 'View Reports', description: 'Access performance reports', priority: 'high' },
  ];

  return (
    <div className="qac-card">
      <h3 className="qac-title">Quick Actions</h3>
      <div className="qac-grid">
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <div key={index} className={`qac-item qac-${action.priority}`}>
              <div className="qac-icon">
                <Icon size={24} />
              </div>
              <div className="qac-content">
                <h4>{action.label}</h4>
                <p>{action.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const CommunicationCard = () => {
  const { user, orgId } = useAuth();
  const navigate = useNavigate();
  const userId = user?.userId;

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardMessages', userId, orgId],
    queryFn: () => fetchMailboxItems({ userId, orgId, size: 4 }),
    enabled: !!userId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const messages = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((item) => {
      const rawName = item.senderName || item.senderId || 'Notification';
      const name = stripHtml(rawName) || 'Notification';
      const initials = name.split(' ').map((part) => part[0]).join('').toUpperCase().substring(0, 2) || 'N';
      const colors = ['#8b5cf6', '#10b981', '#3b82f6', '#f97316'];
      const rawText = item.lastMessagePreview || item.subject || item.body || item.content || 'New Message';
      const cleanMessage = stripHtml(rawText);
      return {
        sender: name,
        message: cleanMessage || 'New Message',
        time: item.lastMessageAt || item.createdAt ? new Date(item.lastMessageAt || item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Recently',
        color: colors[Math.abs(name.length % colors.length)],
        type: initials,
      };
    });
  }, [data]);

  return (
    <div className="com-card">
      <div className="com-header">
        <h3>Communication</h3>
        <button className="com-view-all" onClick={() => navigate('/erp-inbox')}>View All</button>
      </div>
      <div className="com-list">
        {isLoading ? (
          <div className="com-loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <EmptyState icon={MessageSquare} title="No messages" description="Your communication inbox is clear." containerHeight="160px" />
        ) : (
          messages.map((message, index) => (
            <div key={index} className="com-item">
              <div className="com-avatar" style={{ backgroundColor: message.color }}>{message.type}</div>
              <div className="com-content">
                <div className="com-sender">{message.sender}</div>
                <div className="com-message">{message.message}</div>
              </div>
              <div className="com-time">{message.time}</div>
            </div>
          ))
        )}
      </div>
      <div className="com-footer" onClick={() => navigate('/erp-inbox')}>
        <MessageCircle size={20} className="com-icon" />
        <span>Open Inbox</span>
      </div>
    </div>
  );
};

export const AssignmentTrackerCard = () => {
  const assignments = [
    { title: 'Quadratic Equations', class: 'Class 8A', subject: 'Mathematics', daysLeft: 2, status: 'pending', count: 3 },
    { title: "Newton's Laws", class: 'Class 9A', subject: 'Physics', daysLeft: 5, status: 'due', count: 18 },
    { title: 'Trigonometry', class: 'Class 10A', subject: 'Mathematics', daysLeft: 0, status: 'overdue', count: 30 },
  ];

  return (
    <div className="atc-card">
      <div className="atc-header">
        <h3>Assignment Tracker</h3>
        <p>Class 8A - Mathematics</p>
      </div>
      <div className="atc-stats">
        <div className="atc-stat"><span className="atc-number">3</span><span className="atc-label">New</span></div>
        <div className="atc-stat"><span className="atc-number">12</span><span className="atc-label">Pending Review</span></div>
        <div className="atc-stat"><span className="atc-number">25</span><span className="atc-label">Completed</span></div>
      </div>
      <div className="atc-list">
        {assignments.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No assignments" description="You haven't posted any assignments yet." containerHeight="120px" />
        ) : (
          assignments.map((assignment, index) => (
            <div key={index} className={`atc-item atc-${assignment.status}`}>
              <div className="atc-title">{assignment.title}</div>
              <div className="atc-meta">
                <span>{assignment.class}</span>
                <span>{assignment.subject}</span>
              </div>
              <div className="atc-count">{assignment.count} submitted</div>
              <div className="atc-days">
                <span className={`atc-status atc-${assignment.status}`}>
                  {assignment.status === 'overdue' ? 'Overdue' : assignment.status === 'due' ? `${assignment.daysLeft} days` : 'Pending'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      <button className="atc-upload-btn">Upload/Review</button>
    </div>
  );
};

export const QuickNotesCard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.userId;

  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNotePriority, setNewNotePriority] = useState('MEDIUM');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editId, setEditId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editPriority, setEditPriority] = useState('MEDIUM');

  const { data, isLoading: loading } = useQuery({
    queryKey: ['quickNotes', userId],
    queryFn: () => fetchUserNotes(userId),
    enabled: !!userId,
  });

  const notes = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((note) => ({
      id: note.quickNoteId,
      title: note.noteTitle,
      content: note.noteContent,
      priority: note.priorityStatus || 'MEDIUM',
      completed: false,
    }));
  }, [data]);

  const addMutation = useMutation({
    mutationFn: createNote,
    onSuccess: () => {
      queryClient.invalidateQueries(['quickNotes', userId]);
      toast.success('Note added successfully!');
      setNewNoteTitle('');
      setNewNoteContent('');
      setNewNotePriority('MEDIUM');
      setShowNoteForm(false);
    },
    onError: () => toast.error('Failed to add note'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateNote(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['quickNotes', userId]);
      setEditingIndex(null);
      toast.success('Note updated successfully!');
    },
    onError: () => toast.error('Failed to update note'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNoteApi,
    onSuccess: () => {
      queryClient.invalidateQueries(['quickNotes', userId]);
      toast.success('Note deleted successfully!');
    },
    onError: () => toast.error('Failed to delete note'),
  });

  const addNote = () => {
    if (!newNoteTitle.trim() || !newNoteContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    const now = new Date().toISOString();
    addMutation.mutate({
      createdBy: 'FacultyPortal',
      createdAt: now,
      lastUpdatedBy: user?.firstName || 'Faculty',
      lastUpdatedAt: now,
      userId,
      noteTitle: newNoteTitle,
      noteContent: newNoteContent,
      priorityStatus: newNotePriority,
    });
  };

  const startEdit = (index) => {
    const note = notes[index];
    setEditingIndex(index);
    setEditId(note.id);
    setEditTitle(note.title);
    setEditContent(note.content);
    setEditPriority(note.priority);
  };

  const saveEdit = () => {
    if (!editTitle.trim() || !editContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    updateMutation.mutate({
      id: editId,
      payload: {
        quickNoteId: editId,
        userId,
        noteTitle: editTitle,
        noteContent: editContent,
        priorityStatus: editPriority,
        lastUpdatedBy: user?.firstName || 'Faculty',
        lastUpdatedAt: new Date().toISOString(),
      },
    });
  };

  const deleteNote = (index) => {
    if (!window.confirm('Delete this note permanently?')) return;
    deleteMutation.mutate(notes[index].id);
  };

  const isAnyMutationPending = addMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="qnc-card">
      <div className="qnc-header">
        <h3>Quick Notes</h3>
      </div>
      {(loading || isAnyMutationPending) && <MiniLoader message="Syncing Notes" />}
      <div className="qnc-list">
        {notes.length === 0 ? (
          <EmptyState icon={PenTool} title="No notes" description="Quickly jot down your ideas or to-dos." containerHeight="150px" />
        ) : (
          notes.map((note, index) => (
            <div key={note.id || index} className={`qnc-item ${note.completed ? 'completed' : ''}`}>
              {editingIndex === index ? (
                <div className="qnc-edit-mode flex-1">
                  <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="qnc-input" placeholder="Title" autoFocus />
                  <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="qnc-textarea" rows={2} />
                  <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)} className="qnc-select">
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                  <div className="qnc-form-buttons mt-2">
                    <button onClick={saveEdit} className="qnc-save-btn" disabled={loading || isAnyMutationPending}>Save</button>
                    <button onClick={() => setEditingIndex(null)} className="qnc-cancel-btn">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="qnc-content">
                    <h4>{note.title}</h4>
                    <p>{note.content}</p>
                    <span className={`qnc-priority qnc-${note.priority.toLowerCase()}`}>{note.priority}</span>
                  </div>
                  <div className="qnc-actions">
                    <button onClick={() => startEdit(index)} title="Edit"><FaEdit /></button>
                    <button onClick={() => deleteNote(index)} title="Delete"><FaTrashAlt /></button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
      {showNoteForm ? (
        <div className="qnc-form">
          <input type="text" placeholder="Note Title" value={newNoteTitle} onChange={(e) => setNewNoteTitle(e.target.value)} className="qnc-input" />
          <textarea placeholder="Note Content" value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} className="qnc-textarea" />
          <select value={newNotePriority} onChange={(e) => setNewNotePriority(e.target.value)} className="qnc-select">
            <option value="HIGH">High Priority</option>
            <option value="MEDIUM">Medium Priority</option>
            <option value="LOW">Low Priority</option>
          </select>
          <div className="qnc-form-buttons">
            <button onClick={addNote} className="qnc-save-btn" disabled={loading || isAnyMutationPending}>Save Note</button>
            <button onClick={() => setShowNoteForm(false)} className="qnc-cancel-btn">Cancel</button>
          </div>
        </div>
      ) : (
        <button className="qnc-add-btn" onClick={() => setShowNoteForm(true)}>
          <Plus size={16} /> Add New Note
        </button>
      )}
    </div>
  );
};
